import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Check, X, Trash2, Calendar, MessageSquareQuote, Loader2, FileText, Send, Plus, Printer } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "";

export default function AdminDashboard() {
  const { admin, logout, authHeader } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, t, p] = await Promise.all([
        axios.get(`${API}/bookings`, { headers: authHeader() }),
        axios.get(`${API}/admin/testimonials`, { headers: authHeader() }),
        axios.get(`${API}/admin/prescriptions`, { headers: authHeader() }),
      ]);
      setBookings(b.data || []);
      setTestimonials(t.data || []);
      setPrescriptions(p.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        navigate("/admin/login");
      } else {
        toast.error("Failed to load data.");
      }
    } finally {
      setLoading(false);
    }
  }, [authHeader, logout, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const setApproval = async (id, approved) => {
    try {
      await axios.patch(`${API}/admin/testimonials/${id}`, { approved }, { headers: authHeader() });
      setTestimonials((list) => list.map((t) => (t.id === id ? { ...t, approved } : t)));
      toast.success(approved ? "Review published." : "Review hidden.");
    } catch {
      toast.error("Action failed.");
    }
  };

  const deleteTestimonial = async (id) => {
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, { headers: authHeader() });
      setTestimonials((list) => list.filter((t) => t.id !== id));
      toast.success("Review deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const deleteBooking = async (id) => {
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, { headers: authHeader() });
      setBookings((list) => list.filter((b) => b.id !== id));
      toast.success("Booking removed.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const resendEmail = async (id) => {
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${id}/resend-email`, {}, { headers: authHeader() });
      setBookings((list) => list.map((b) => (b.id === id ? { ...b, email_sent: data.email_sent, confirmation_sent: data.confirmation_sent } : b)));
      if (data.sent) {
        toast.success("Emails sent to you and the patient.");
      } else {
        toast.error("Email delivery failed. Please try again shortly.");
      }
    } catch {
      toast.error("Resend failed.");
    }
  };

  const resendAllPending = async () => {
    setResending(true);
    try {
      const { data } = await axios.post(`${API}/admin/bookings/resend-pending`, {}, { headers: authHeader() });
      if (data.sent > 0) {
        setBookings((list) => list.map((b) => data.sent_ids.includes(b.id) ? { ...b, email_sent: true } : b));
        toast.success(`${data.sent} email(s) sent. ${data.failed} still failed.`);
      } else {
        toast.error("No emails could be sent. Email service not configured.");
      }
    } catch {
      toast.error("Resend failed.");
    } finally {
      setResending(false);
    }
  };

  const deletePrescription = async (id) => {
    if (!window.confirm("Delete this prescription?")) return;
    try {
      await axios.delete(`${API}/admin/prescriptions/${id}`, { headers: authHeader() });
      setPrescriptions((list) => list.filter((p) => p.id !== id));
      toast.success("Prescription deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const pendingCount = testimonials.filter((t) => !t.approved).length;
  const missedEmails = bookings.filter((b) => !b.email_sent).length;

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="admin-dashboard">
      <header className="bg-[#2C3C30] text-[#F7F6F2]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl tracking-tight">Adya Admin</h1>
            <p className="font-sans text-sm text-[#D1D6CC]">
              {admin?.email}
            </p>
          </div>
          <button
            onClick={() => { logout(); navigate("/admin/login"); }}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#F7F6F2]/10 hover:bg-[#F7F6F2]/20 px-5 py-2.5 text-sm font-medium transition-colors"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10">
        <div className="flex gap-3 mb-8 flex-wrap">
          <button
            onClick={() => setTab("bookings")}
            data-testid="tab-bookings"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
              tab === "bookings" ? "bg-[#2C3C30] text-[#F7F6F2]" : "bg-[#EAE7E0] text-[#2C3C30]"
            }`}
          >
            <Calendar size={16} /> Bookings ({bookings.length})
            {missedEmails > 0 && (
              <span className="ml-1 rounded-full bg-[#a13a2f] text-[#F7F6F2] text-xs px-2 py-0.5">
                {missedEmails} missed
              </span>
            )}
          </button>
          <button
            onClick={() => setTab("testimonials")}
            data-testid="tab-testimonials"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
              tab === "testimonials" ? "bg-[#2C3C30] text-[#F7F6F2]" : "bg-[#EAE7E0] text-[#2C3C30]"
            }`}
          >
            <MessageSquareQuote size={16} /> Reviews{pendingCount ? ` (${pendingCount} pending)` : ""}
          </button>
          <button
            onClick={() => setTab("prescriptions")}
            data-testid="tab-prescriptions"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
              tab === "prescriptions" ? "bg-[#2C3C30] text-[#F7F6F2]" : "bg-[#EAE7E0] text-[#2C3C30]"
            }`}
          >
            <FileText size={16} /> Prescriptions ({prescriptions.length})
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[#566158] font-sans py-20 justify-center">
            <Loader2 className="animate-spin" size={20} /> Loading...
          </div>
        ) : tab === "bookings" ? (
          <div className="space-y-4" data-testid="bookings-list">
            {missedEmails > 0 && (
              <div className="flex items-center justify-between gap-4 bg-[#a13a2f]/10 border border-[#a13a2f]/30 rounded-2xl p-5">
                <div>
                  <p className="font-serif text-xl text-[#a13a2f]">
                    {missedEmails} booking{missedEmails > 1 ? "s" : ""} awaiting email delivery
                  </p>
                  <p className="font-sans text-sm text-[#566158]">
                    Retry sending these notifications once your email provider is configured.
                  </p>
                </div>
                <button
                  onClick={resendAllPending}
                  disabled={resending}
                  data-testid="resend-all-pending"
                  className="inline-flex items-center gap-2 rounded-full bg-[#2C3C30] text-[#F7F6F2] px-5 py-2.5 text-sm font-medium hover:bg-[#1A211D] disabled:opacity-60"
                >
                  {resending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  Resend all
                </button>
              </div>
            )}
            {bookings.length === 0 && (
              <p className="font-sans text-[#566158] py-10">No bookings yet.</p>
            )}
            {bookings.map((b) => (
              <div
                key={b.id}
                data-testid={`booking-row-${b.id}`}
                className="bg-white rounded-2xl border border-[#2C3C30]/10 p-6 flex flex-col md:flex-row md:items-center gap-4"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-serif text-2xl text-[#2C3C30]">{b.name}</span>
                    <span className="font-sans text-xs uppercase tracking-wide text-[#566158] border border-[#2C3C30]/20 rounded-full px-3 py-1">
                      {b.concern}
                    </span>
                    <span
                      className={`font-sans text-xs uppercase tracking-wide rounded-full px-3 py-1 ${
                        b.email_sent
                          ? "bg-[#EAE7E0] text-[#566158]"
                          : "bg-[#a13a2f]/10 text-[#a13a2f]"
                      }`}
                    >
                      {b.email_sent ? "Emailed" : "Follow up manually"}
                    </span>
                    <span
                      data-testid={`confirmation-status-${b.id}`}
                      className={`font-sans text-xs uppercase tracking-wide rounded-full px-3 py-1 ${
                        b.confirmation_sent
                          ? "bg-[#EAE7E0] text-[#566158]"
                          : "bg-[#a13a2f]/10 text-[#a13a2f]"
                      }`}
                    >
                      {b.confirmation_sent ? "Patient confirmed" : "Patient not emailed"}
                    </span>
                  </div>
                  <div className="font-sans text-[#566158] mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                    <a href={`tel:${b.phone}`} className="text-[#2C3C30] font-medium">{b.phone}</a>
                    <span>{b.email}</span>
                    <span>Preferred: {b.preferred_date || "—"} {b.preferred_time || ""}</span>
                    <span>Requested: {fmt(b.created_at)}</span>
                  </div>
                  {b.message && (
                    <p className="font-sans text-[#566158] mt-2 text-sm italic">“{b.message}”</p>
                  )}
                </div>
                <div className="flex items-center gap-2 self-start">
                  {!b.email_sent && (
                    <button
                      onClick={() => resendEmail(b.id)}
                      data-testid={`resend-booking-${b.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#2C3C30] text-[#F7F6F2] px-4 py-2 text-sm font-medium hover:bg-[#1A211D] transition-colors"
                    >
                      <Send size={14} /> Resend
                    </button>
                  )}
                  <button
                    onClick={() => deleteBooking(b.id)}
                    data-testid={`delete-booking-${b.id}`}
                    className="text-[#a13a2f] hover:bg-[#a13a2f]/10 rounded-full p-2.5 transition-colors"
                    aria-label="Delete booking"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : tab === "testimonials" ? (
          <div className="space-y-4" data-testid="testimonials-list">
            {testimonials.length === 0 && (
              <p className="font-sans text-[#566158] py-10">No reviews yet.</p>
            )}
            {testimonials.map((t) => (
              <div
                key={t.id}
                data-testid={`testimonial-row-${t.id}`}
                className="bg-white rounded-2xl border border-[#2C3C30]/10 p-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-serif text-2xl text-[#2C3C30]">{t.name}</span>
                      <span className="font-sans text-sm text-[#566158]">
                        {[t.location, t.concern].filter(Boolean).join(" · ")}
                      </span>
                      <span
                        className={`font-sans text-xs uppercase tracking-wide rounded-full px-3 py-1 ${
                          t.approved ? "bg-[#2C3C30] text-[#F7F6F2]" : "bg-[#EAE7E0] text-[#566158]"
                        }`}
                      >
                        {t.approved ? "Published" : "Pending"}
                      </span>
                    </div>
                    <p className="font-sans text-[#1A211D] mt-3">“{t.quote}”</p>
                    <p className="font-sans text-xs text-[#7D8C7A] mt-2">Rating: {t.rating}/5 · {fmt(t.created_at)}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {t.approved ? (
                      <button
                        onClick={() => setApproval(t.id, false)}
                        data-testid={`hide-testimonial-${t.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#EAE7E0] text-[#2C3C30] px-4 py-2 text-sm font-medium hover:bg-[#D1D6CC] transition-colors"
                      >
                        <X size={15} /> Hide
                      </button>
                    ) : (
                      <button
                        onClick={() => setApproval(t.id, true)}
                        data-testid={`approve-testimonial-${t.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#2C3C30] text-[#F7F6F2] px-4 py-2 text-sm font-medium hover:bg-[#1A211D] transition-colors"
                      >
                        <Check size={15} /> Approve
                      </button>
                    )}
                    <button
                      onClick={() => deleteTestimonial(t.id)}
                      data-testid={`delete-testimonial-${t.id}`}
                      className="text-[#a13a2f] hover:bg-[#a13a2f]/10 rounded-full p-2 transition-colors"
                      aria-label="Delete review"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4" data-testid="prescriptions-list">
            <div className="flex justify-between items-center gap-4 mb-2">
              <p className="font-sans text-sm text-[#566158]">
                Create diet charts and prescriptions in the name of Adya Nutrition.
              </p>
              <Link
                to="/admin/prescriptions/new"
                data-testid="new-prescription-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#2C3C30] text-[#F7F6F2] px-5 py-2.5 text-sm font-medium hover:bg-[#1A211D] transition-colors"
              >
                <Plus size={16} /> New prescription
              </Link>
            </div>
            {prescriptions.length === 0 && (
              <p className="font-sans text-[#566158] py-10">No prescriptions yet.</p>
            )}
            {prescriptions.map((p) => (
              <div
                key={p.id}
                data-testid={`prescription-row-${p.id}`}
                className="bg-white rounded-2xl border border-[#2C3C30]/10 p-6 flex flex-col md:flex-row md:items-center gap-4"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-serif text-2xl text-[#2C3C30]">{p.patient_name}</span>
                    <span className="font-sans text-xs uppercase tracking-wide text-[#7D8C7A]">
                      {p.number}
                    </span>
                  </div>
                  <div className="font-sans text-[#566158] mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                    {p.patient_phone && <span>{p.patient_phone}</span>}
                    {p.patient_email && <span>{p.patient_email}</span>}
                    <span>Consult date: {p.consultation_date}</span>
                    <span>Issued: {fmt(p.created_at)}</span>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Link
                    to={`/admin/prescriptions/${p.id}`}
                    data-testid={`view-prescription-${p.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#2C3C30] text-[#F7F6F2] px-4 py-2 text-sm font-medium hover:bg-[#1A211D] transition-colors"
                  >
                    <Printer size={14} /> View / Print
                  </Link>
                  <button
                    onClick={() => deletePrescription(p.id)}
                    data-testid={`delete-prescription-${p.id}`}
                    className="text-[#a13a2f] hover:bg-[#a13a2f]/10 rounded-full p-2 transition-colors"
                    aria-label="Delete prescription"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
